import type { IntlShape } from 'react-intl';

import { ensureIsoTimezone, formatRelativeTimeFromNow } from '@heidi/utils/dates';

import type { WorkflowRun } from '../../../contracts/routines.types';

/** Non-component helpers shared by the minimal routine overview sections. */

export const SECTION_CARD = 'rounded-xl border border-border bg-surface';

/**
 * Most recent runs shown on the overview's Previous-runs rail and the run
 * chat's Recent-runs card before History takes over. Shared so both surfaces
 * hit the same ``useWorkflowRuns`` cache entry (one fetch, not two).
 */
export const RECENT_RUNS_LIMIT = 8;

/**
 * Rows the run chat's Recent-runs card shows before "See all" takes over. Lower
 * than {@link RECENT_RUNS_LIMIT} on purpose: the query stays at 8 so the card
 * shares the overview's cache entry, but the card only lists 5 — the point is
 * "what happened lately", not a history view.
 */
export const RECENT_RUNS_RAIL_VISIBLE = 5;

/** Focus ring for the interactive value side of a section row. `min-w-0` lets
 * the trigger shrink inside its flex row so long values truncate instead of
 * pushing the row wider than the card. The named group drives `RowValue`'s
 * hover promotion, so static values (no trigger ancestor) stay quiet. */
export const ROW_TRIGGER_CLASSES =
  'group/row-trigger flex min-w-0 items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-border-focus disabled:pointer-events-none';

/**
 * Defensive newest-first re-order of a runs page. The backend already returns
 * runs newest-first (History renders the sibling all-runs listing in server
 * order), so this only guards the run rails against an unordered page.
 */
export function sortRunsNewestFirst(runs: WorkflowRun[]): WorkflowRun[] {
  return [...runs].sort((a, b) => {
    const ta = a.created_at ? Date.parse(ensureIsoTimezone(a.created_at)) : 0;
    const tb = b.created_at ? Date.parse(ensureIsoTimezone(b.created_at)) : 0;
    return tb - ta;
  });
}

/**
 * Localized relative age for the Previous-runs rail, e.g. "3 minutes ago",
 * "8分钟前". Backend run timestamps are often naive; `formatRelativeTimeFromNow`
 * (via `ensureIsoTimezone`) treats a timezone-less timestamp as UTC to match
 * other run surfaces.
 *
 * @param locale - App locale code. Omit only where no locale is in scope; the
 *   result is then English regardless of the user's language.
 */
export function formatRunAge(iso: string | null, locale?: string): string {
  return formatRelativeTimeFromNow(iso, locale);
}

/** "9:00 AM"-style label for the schedule's start time; null when unset so
 * the caller supplies its own localized fallback. */
export function timeLabel(date: Date | undefined, intl: IntlShape): string | null {
  if (!date) return null;
  return intl.formatTime(date, { hour: 'numeric', minute: '2-digit' });
}

/** Half-hour slots for the "At" dropdown (labels built via intl at the call site). */
export const TIME_SLOTS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2);
  const minutes = i % 2 === 0 ? 0 : 30;
  return {
    key: `${hours}:${String(minutes).padStart(2, '0')}`,
    hours,
    minutes,
  };
});

/** Half-hour time options for the "At" dropdown, labeled with the app locale. */
export function getTimeOptions(intl: IntlShape): Array<{
  key: string;
  hours: number;
  minutes: number;
  label: string;
}> {
  return TIME_SLOTS.map((slot) => {
    const date = new Date();
    date.setHours(slot.hours, slot.minutes, 0, 0);
    return {
      ...slot,
      label: intl.formatTime(date, { hour: 'numeric', minute: '2-digit' }),
    };
  });
}
